import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Grid, Card, CardContent, Alert, Button,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  NotificationsActive, Error, CheckCircle, Email, Sms,
  Refresh, FilterList, TrendingUp
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { alertsAPI } from '../../services/api';

const Alerts = () => {
  const { tenantId } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    limit: 50
  });

  useEffect(() => {
    loadAlerts();
    loadRecentEvents();
    loadStats();
    
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(() => {
      loadAlerts();
      loadRecentEvents();
      loadStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [tenantId, filters]);

  const loadAlerts = async () => {
    try {
      const response = await alertsAPI.getHistory(filters);
      setAlerts(response.data?.data || []);
    } catch (error) {
      console.error('Error loading alerts:', error);
      setAlerts([]);
    }
  };

  const loadRecentEvents = async () => {
    try {
      const response = await alertsAPI.getRecentEvents({ limit: 10 });
      setRecentEvents(response.data?.data || []);
    } catch (error) {
      console.error('Error loading recent events:', error);
      setRecentEvents([]);
    }
  };

  const loadStats = async () => {
    try {
      const response = await alertsAPI.getStats();
      setStats(response.data?.data || {});
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ total_alerts_today: 0, critical_alerts: 0, pending_alerts: 0, acknowledged_alerts: 0, email_sent: 0, sms_sent: 0 });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'expired': return 'error';
      case 'expiring': return 'warning';
      case 'valid': return 'success';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'critical': return 'error';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora';
    if (diffMinutes < 60) return `${diffMinutes}min atrás`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  const handleAcknowledge = (alertId) => {
    setAlerts(alerts.map(alert => 
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    ));
  };

  const handleTestAlert = async () => {
    try {
      const testAlertData = {
        equipment_id: 'test-equipment',
        barrier_id: 'test-barrier',
        status: 'expired',
        category: 'critical',
        message: 'Alerta de teste do sistema TrackHealth'
      };
      
      await alertsAPI.sendAlert(testAlertData);
      
      // Reload data
      loadAlerts();
      loadStats();
      
      alert('Alerta de teste enviado com sucesso!');
    } catch (error) {
      console.error('Error sending test alert:', error);
      alert('Erro ao enviar alerta de teste');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <NotificationsActive sx={{ mr: 1 }} />
        Central de Alertas
      </Typography>

      {/* Alertas Ativos */}
      {alerts.filter(a => !a.acknowledged && a.category === 'critical').length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          🚨 {alerts.filter(a => !a.acknowledged && a.category === 'critical').length} alertas críticos pendentes!
        </Alert>
      )}

      {/* Estatísticas */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Alertas Hoje
              </Typography>
              <Typography variant="h4">
                {stats.total_alerts_today || 0}
              </Typography>
              <Typography variant="body2" color="success.main">
                <TrendingUp sx={{ fontSize: 16 }} /> +15% vs ontem
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Críticos Pendentes
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.critical_alerts || 0}
              </Typography>
              <Typography variant="body2">
                <Error sx={{ fontSize: 16 }} /> Ação necessária
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Emails Enviados
              </Typography>
              <Typography variant="h4">
                {stats.email_sent || 0}
              </Typography>
              <Typography variant="body2">
                <Email sx={{ fontSize: 16 }} /> Últimas 24h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                SMS Enviados
              </Typography>
              <Typography variant="h4">
                {stats.sms_sent || 0}
              </Typography>
              <Typography variant="body2">
                <Sms sx={{ fontSize: 16 }} /> Últimas 24h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controles */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <FilterList sx={{ mr: 1 }} />
          Controles
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="expired">Vencido</MenuItem>
                <MenuItem value="expiring">Vencendo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="critical">Crítico</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="low">Baixo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={() => {loadAlerts(); loadRecentEvents(); loadStats();}}
              fullWidth
            >
              Atualizar
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleTestAlert}
              fullWidth
            >
              Teste Alerta
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Alertas */}
      <Paper sx={{ mb: 3 }}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Alertas Ativos
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Equipamento</TableCell>
                <TableCell>Barreira</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Canais</TableCell>
                <TableCell>Horário</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow 
                  key={alert.id}
                  sx={{ 
                    backgroundColor: alert.acknowledged ? 'transparent' : 
                      alert.category === 'critical' ? 'error.light' : 'warning.light',
                    opacity: alert.acknowledged ? 0.7 : 1
                  }}
                >
                  <TableCell>
                    <Typography variant="subtitle2">
                      {alert.equipment_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{alert.barrier_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={alert.status}
                      color={getStatusColor(alert.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={alert.category}
                      color={getCategoryColor(alert.category)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      {alert.channels_sent.includes('email') && (
                        <Email color="primary" fontSize="small" />
                      )}
                      {alert.channels_sent.includes('sms') && (
                        <Sms color="secondary" fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatTimestamp(alert.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {!alert.acknowledged ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAcknowledge(alert.id)}
                      >
                        Reconhecer
                      </Button>
                    ) : (
                      <CheckCircle color="success" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Eventos Recentes */}
      <Paper>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Eventos Recentes
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Equipamento</TableCell>
                <TableCell>Barreira</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Alerta Enviado</TableCell>
                <TableCell>Horário</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{event.equipment_name}</TableCell>
                  <TableCell>{event.barrier_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={event.status}
                      color={getStatusColor(event.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {event.alert_sent ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Não enviado
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatTimestamp(event.timestamp)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Alerts;